import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Directory from "@/pages/Directory";
import BusinessDetail from "@/pages/BusinessDetail";
import BusinessDashboard from "@/pages/BusinessDashboard";
import BusinessEdit from "@/pages/BusinessEdit";
import CustomerDashboard from "@/pages/CustomerDashboard";
import CustomerDetail from "@/pages/CustomerDetail";
import Subscription from "@/pages/Subscription";
import PaymentSuccess from "@/pages/PaymentSuccess";
import ListYourBusiness from "@/pages/ListYourBusiness";
import AdminPanel from "@/pages/AdminPanel";
import Offers from "@/pages/Offers";
import Categories from "@/pages/Categories";
import ReferralLanding from "@/pages/ReferralLanding";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PrivacyDisclaimer from "@/pages/PrivacyDisclaimer";
import TermsOfService from "@/pages/TermsOfService";
import Messages from "@/pages/Messages";
import EmbedBusiness from "@/pages/EmbedBusiness";
import Neighborhood from "@/pages/Neighborhood";
import FindNeighborhood from "@/pages/FindNeighborhood";
import Marketplace from "@/pages/Marketplace";
import Auctions from "@/pages/Auctions";
import Shops from "@/pages/Shops";
import BusinessShop from "@/pages/BusinessShop";
import ProductDetail from "@/pages/ProductDetail";
import ShopManage from "@/pages/ShopManage";
import ShopPurchaseSuccess from "@/pages/ShopPurchaseSuccess";
import Events from "@/pages/Events";
import FoodTrucks from "@/pages/FoodTrucks";
import MobileManage from "@/pages/MobileManage";
import MarketplaceLive from "@/pages/MarketplaceLive";
import MarketplaceLiveNew from "@/pages/MarketplaceLiveNew";
import MarketplaceLiveWatch from "@/pages/MarketplaceLiveWatch";
import ConcertDetail from "@/pages/ConcertDetail";
import ConcertManage from "@/pages/ConcertManage";
import ConcertPurchaseSuccess from "@/pages/ConcertPurchaseSuccess";
import MyTickets from "@/pages/MyTickets";
import MarketplaceSell from "@/pages/MarketplaceSell";
import MarketplaceListing from "@/pages/MarketplaceListing";
import MarketplaceMyListings from "@/pages/MarketplaceMyListings";
import MarketplacePurchaseSuccess from "@/pages/MarketplacePurchaseSuccess";
import MarketplaceOnboarding from "@/pages/MarketplaceOnboarding";
import PlaylistsV2 from "@/pages/PlaylistsV2";
import ArtistPage from "@/pages/ArtistPage";
import InfluencerSubscribe from "@/pages/InfluencerSubscribe";
import MyOffers from "@/pages/MyOffers";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-gray-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function Shell() {
  return (
    <div className="App">
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/directory" element={<Directory />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/offers" element={<Offers />} />
          <Route path="/list-your-business" element={<ListYourBusiness />} />
          <Route path="/businesses/:id" element={<BusinessDetail />} />
          <Route path="/r/:code" element={<ReferralLanding />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/privacy" element={<PrivacyDisclaimer />} />
          <Route path="/privacy-disclaimer" element={<Navigate to="/privacy" replace />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
          <Route path="/dashboard/business" element={<Protected role="business"><BusinessDashboard /></Protected>} />
          <Route path="/dashboard/business/edit" element={<Protected role="business"><BusinessEdit /></Protected>} />
          <Route path="/dashboard/customer" element={<Protected role="customer"><CustomerDashboard /></Protected>} />
          <Route path="/messages" element={<Protected><Messages /></Protected>} />
          <Route path="/neighborhood" element={<Protected role="customer"><Neighborhood /></Protected>} />
          <Route path="/find-neighborhood" element={<FindNeighborhood />} />
          <Route path="/find-neighborhood/:stateNorm" element={<FindNeighborhood />} />
          <Route path="/find-neighborhood/:stateNorm/:cityNorm" element={<FindNeighborhood />} />
          <Route path="/find-neighborhood/:stateNorm/:cityNorm/:zip" element={<FindNeighborhood />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/shops" element={<Shops />} />
          <Route path="/shop/manage" element={<Protected><ShopManage /></Protected>} />
          <Route path="/shop/purchase-success" element={<Protected><ShopPurchaseSuccess /></Protected>} />
          <Route path="/shop/:business_id" element={<BusinessShop />} />
          <Route path="/shop/:business_id/product/:pid" element={<ProductDetail />} />
          <Route path="/auctions" element={<Auctions />} />
          <Route path="/events" element={<Events />} />
          <Route path="/foodtrucks" element={<FoodTrucks />} />
          <Route path="/mobile/manage" element={<Protected><MobileManage /></Protected>} />
          <Route path="/marketplace/live" element={<MarketplaceLive />} />
          <Route path="/marketplace/live/new" element={<Protected><MarketplaceLiveNew /></Protected>} />
          <Route path="/marketplace/live/:sid" element={<MarketplaceLiveWatch />} />
          <Route path="/concerts/new" element={<Protected><ConcertManage /></Protected>} />
          <Route path="/concerts/purchase-success" element={<Protected><ConcertPurchaseSuccess /></Protected>} />
          <Route path="/concerts/:cid" element={<ConcertDetail />} />
          <Route path="/concerts/:cid/manage" element={<Protected><ConcertManage /></Protected>} />
          <Route path="/my/tickets" element={<Protected><MyTickets /></Protected>} />
          <Route path="/marketplace/onboard" element={<Protected><MarketplaceOnboarding /></Protected>} />
          <Route path="/playlists" element={<Protected><PlaylistsV2 /></Protected>} />
          <Route path="/artists/:aid" element={<ArtistPage />} />
          <Route path="/marketplace/sell" element={<Protected><MarketplaceSell /></Protected>} />
          <Route path="/marketplace/sell/:lid" element={<Protected><MarketplaceSell /></Protected>} />
          <Route path="/marketplace/my" element={<Protected><MarketplaceMyListings /></Protected>} />
          <Route path="/marketplace/purchase-success" element={<Protected><MarketplacePurchaseSuccess /></Protected>} />
          <Route path="/marketplace/offers" element={<Protected><MyOffers /></Protected>} />
          <Route path="/marketplace/listings/:lid" element={<MarketplaceListing />} />
          <Route path="/customers/:id" element={<CustomerDetail />} />
          <Route path="/subscription" element={<Protected role="business"><Subscription /></Protected>} />
          <Route path="/subscription/success" element={<Protected role="business"><PaymentSuccess /></Protected>} />
          <Route path="/influencer/subscribe" element={<Protected><InfluencerSubscribe /></Protected>} />
          <Route path="/admin" element={<Protected role="admin"><AdminPanel /></Protected>} />
        </Routes>
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Embed routes render bare — no navbar/footer so hosts can iframe them cleanly */}
          <Route path="/embed/business/:id" element={<EmbedBusiness />} />
          <Route path="*" element={<Shell />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
